import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { Button } from '@/components/ui/Button';
import { adminQuizzesService } from '@/services/admin-quizzes.service';
import { adminArticlesService } from '@/services/admin-articles.service';
import { ApiError } from '@/services/http';
import { cn } from '@/lib/cn';
import type { QuizStatus, QuizView } from '@/types/api';

/**
 * AdminEditQuizPage — `/admin/quizzes/:id` for existing quizzes and
 * `/admin/quizzes/new?bookId=…` / `?articleId=…` for new.
 *
 * Layout:
 *   - Top bar: back link (to the parent book/article editor), parent
 *     label, unsaved indicator, Save + Delete buttons.
 *   - Sidebar (right): status, pass threshold, intro copy.
 *   - Main: N question blocks (default 5) each with prompt, 4 option
 *     inputs with a "correct" radio, explanation, optional callback.
 *
 * "Save quiz" runs PATCH metadata + PUT questions in sequence (when
 * editing). On create, a single POST /admin/quizzes writes everything
 * in one transaction.
 *
 * Note: the original sidebar had badge/creature fields (id, name,
 * emoji) — those were retired along with the badge concept. Coins are
 * the sole reward now, configured process-wide via the server's
 * `QUIZ_COIN_REWARD` env var, so there's nothing per-quiz to edit on
 * that axis.
 */

type ParentKind = 'book' | 'article';

interface OptionDraft {
  id: string;
  text: string;
  emoji: string;
}

interface QuestionDraft {
  prompt: string;
  explanation: string;
  callback: string;
  options: OptionDraft[];
  correctOptionId: string;
}

interface QuizDraft {
  /** Minimum correctCount that earns coins. 1..5. */
  passThreshold: number;
  status: QuizStatus;
  questions: QuestionDraft[];
}

const DEFAULT_OPTION_IDS = ['a', 'b', 'c', 'd'] as const;

function emptyOption(id: string): OptionDraft {
  return { id, text: '', emoji: '' };
}

function emptyQuestion(): QuestionDraft {
  return {
    prompt: '',
    explanation: '',
    callback: '',
    options: DEFAULT_OPTION_IDS.map(emptyOption),
    correctOptionId: 'a',
  };
}

function emptyDraft(): QuizDraft {
  return {
    passThreshold: 4,
    status: 'pending',
    questions: Array.from({ length: 5 }, emptyQuestion),
  };
}

function toDraft(quiz: QuizView): QuizDraft {
  return {
    passThreshold: quiz.passThreshold,
    status: quiz.status === 'live' ? 'live' : 'pending',
    questions: quiz.questions.map((q) => ({
      prompt: q.prompt,
      // Draft state uses '' for the empty case so the textarea binds
      // cleanly; persisted as null on save.
      explanation: q.explanation ?? '',
      callback: q.callback ?? '',
      options: q.options.map((o) => ({
        id: o.id,
        text: o.text,
        emoji: o.emoji ?? '',
      })),
      correctOptionId: q.correctOptionId,
    })),
  };
}

function draftsEqual(a: QuizDraft, b: QuizDraft): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

// -------------------------------------------------------------------------

export function AdminEditQuizPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // --- Parent resolution ------------------------------------------------
  const qpBookId = searchParams.get('bookId');
  const qpArticleId = searchParams.get('articleId');
  const [parentKind, setParentKind] = useState<ParentKind | null>(null);
  const [parentId, setParentId] = useState<string | null>(null);
  const [parentTitle, setParentTitle] = useState<string>('');

  // --- Draft / quiz state ----------------------------------------------
  const [source, setSource] = useState<QuizDraft | null>(null);
  const [draft, setDraft] = useState<QuizDraft>(emptyDraft);
  const [quizId, setQuizId] = useState<string | null>(isNew ? null : id!);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // --- Load existing / resolve parent metadata -------------------------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        if (isNew) {
          // Creating from a parent editor query param.
          if (qpBookId) {
            setParentKind('book');
            setParentId(qpBookId);
            setParentTitle('Book');
          } else if (qpArticleId) {
            setParentKind('article');
            setParentId(qpArticleId);
            // Pull the article title so the top bar isn't anonymous.
            try {
              const article = await adminArticlesService.getById(qpArticleId);
              if (!cancelled) setParentTitle(article.title);
            } catch {
              if (!cancelled) setParentTitle('Article');
            }
          } else {
            toast.error('Cannot create a quiz without a parent. Start from a book or article.');
            navigate('/admin/articles', { replace: true });
            return;
          }
          setSource(emptyDraft());
          setDraft(emptyDraft());
          return;
        }

        const quiz = await adminQuizzesService.getById(id!);
        if (cancelled) return;
        const next = toDraft(quiz);
        setSource(next);
        setDraft(next);
        setQuizId(quiz.id);

        if (quiz.bookId) {
          setParentKind('book');
          setParentId(quiz.bookId);
          setParentTitle('Book');
        } else if (quiz.articleId) {
          setParentKind('article');
          setParentId(quiz.articleId);
          try {
            const article = await adminArticlesService.getById(quiz.articleId);
            if (!cancelled) setParentTitle(article.title);
          } catch {
            if (!cancelled) setParentTitle('Article');
          }
        }
      } catch (err) {
        if (cancelled) return;
        toast.error(err instanceof ApiError ? err.message : 'Could not load quiz.');
        navigate('/admin/articles', { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, isNew, qpBookId, qpArticleId, navigate]);

  const dirty = useMemo(() => {
    if (!source) return false;
    return !draftsEqual(source, draft);
  }, [source, draft]);

  // Warn before navigating away with unsaved edits.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // --- Setters --------------------------------------------------------
  const setField = useCallback(<K extends keyof QuizDraft>(key: K, value: QuizDraft[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateQuestion = useCallback((index: number, patch: Partial<QuestionDraft>) => {
    setDraft((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => (i === index ? { ...q, ...patch } : q)),
    }));
  }, []);

  const updateOption = useCallback(
    (qIndex: number, oIndex: number, patch: Partial<OptionDraft>) => {
      setDraft((prev) => ({
        ...prev,
        questions: prev.questions.map((q, qi) =>
          qi !== qIndex
            ? q
            : {
                ...q,
                options: q.options.map((o, oi) => (oi === oIndex ? { ...o, ...patch } : o)),
              },
        ),
      }));
    },
    [],
  );

  const setCorrect = useCallback((qIndex: number, optionId: string) => {
    setDraft((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) =>
        i === qIndex ? { ...q, correctOptionId: optionId } : q,
      ),
    }));
  }, []);

  // --- Save ----------------------------------------------------------
  const handleSave = async () => {
    if (saving) return;

    // Client-side validation before we hit the wire.
    const problems: string[] = [];
    draft.questions.forEach((q, qi) => {
      if (!q.prompt.trim()) problems.push(`Question ${qi + 1}: prompt is required.`);
      // Explanation is optional since the DB column dropped NOT NULL —
      // bulk-imported quizzes (Book Quizzes CSV) ship without one.
      const ids = new Set<string>();
      q.options.forEach((o, oi) => {
        if (!o.text.trim()) problems.push(`Question ${qi + 1} option ${oi + 1}: text required.`);
        if (!o.id.trim()) problems.push(`Question ${qi + 1} option ${oi + 1}: id required.`);
        if (ids.has(o.id)) problems.push(`Question ${qi + 1}: duplicate option id "${o.id}".`);
        ids.add(o.id);
      });
      if (!ids.has(q.correctOptionId)) {
        problems.push(`Question ${qi + 1}: correct option does not match any option id.`);
      }
    });
    if (problems.length > 0) {
      toast.error(problems[0]!, { description: problems.slice(1, 4).join(' · ') });
      return;
    }

    setSaving(true);
    try {
      const questionsPayload = draft.questions.map((q) => ({
        prompt: q.prompt.trim(),
        // null instead of empty string so the DB column stays tidy; the
        // server also coerces this but we'd rather be explicit on the wire.
        explanation: q.explanation.trim() ? q.explanation.trim() : null,
        callback: q.callback.trim() ? q.callback.trim() : null,
        options: q.options.map((o) => ({
          id: o.id.trim(),
          text: o.text.trim(),
          emoji: o.emoji.trim() ? o.emoji.trim() : null,
        })),
        correctOptionId: q.correctOptionId,
      }));

      if (isNew || !quizId) {
        if (!parentKind || !parentId) {
          toast.error('No parent set for this quiz.');
          setSaving(false);
          return;
        }
        const created = await adminQuizzesService.create({
          bookId: parentKind === 'book' ? parentId : null,
          articleId: parentKind === 'article' ? parentId : null,
          passThreshold: draft.passThreshold,
          status: draft.status,
          questions: questionsPayload,
        });
        toast.success('Quiz created.');
        const next = toDraft(created);
        setSource(next);
        setDraft(next);
        setQuizId(created.id);
        navigate(`/admin/quizzes/${created.id}`, { replace: true });
      } else {
        // Update metadata first, then replace questions. Both calls
        // mutate the same row; ordering keeps the common "metadata-only
        // tweak" case cheaper (second call is a no-op if the textarea
        // didn't change — still fires for simplicity).
        await adminQuizzesService.updateMetadata(quizId, {
          passThreshold: draft.passThreshold,
          status: draft.status,
        });
        const updated = await adminQuizzesService.replaceQuestions(quizId, {
          questions: questionsPayload,
        });
        toast.success('Saved.');
        const next = toDraft(updated);
        setSource(next);
        setDraft(next);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!quizId || isNew) return;
    const ok = window.confirm('Delete this quiz? This cannot be undone.');
    if (!ok) return;
    try {
      await adminQuizzesService.remove(quizId);
      toast.success('Quiz deleted.');
      // Route back to the parent editor when possible.
      if (parentKind === 'article' && parentId) {
        navigate(`/admin/articles/${parentId}`);
      } else if (parentKind === 'book' && parentId) {
        navigate(`/admin/books/${parentId}`);
      } else {
        navigate('/admin/articles');
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  if (loading) {
    return (
      <AppShell mode="parent">
        <div className="py-16 text-center text-or-slate-500">Loading…</div>
      </AppShell>
    );
  }

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-6">
        <AdminSectionTabs />

        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                if (parentKind === 'article' && parentId) navigate(`/admin/articles/${parentId}`);
                else if (parentKind === 'book' && parentId) navigate(`/admin/books/${parentId}`);
                else navigate('/admin/articles');
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-or-slate-200 bg-white text-or-slate-600 hover:bg-or-slate-50"
              aria-label="Back to parent editor"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
            </button>
            <div>
              <div className="or-kicker text-or-orange-600">
                {isNew ? 'New quiz' : 'Edit quiz'} · {parentKind === 'book' ? 'Book' : 'Article'}
              </div>
              <h1 className="mt-0.5 font-display text-[22px] font-bold text-or-slate-900 sm:text-[26px]">
                {parentTitle || 'Quiz'}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {dirty ? (
              <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-or-orange-600">
                Unsaved changes
              </span>
            ) : !isNew ? (
              <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-or-slate-400">
                Saved
              </span>
            ) : null}
            {!isNew ? (
              <Button variant="danger" size="md" onClick={handleDelete}>
                <Trash2 className="h-4 w-4" aria-hidden />
                Delete
              </Button>
            ) : null}
            <Button
              variant="primary"
              size="md"
              onClick={handleSave}
              disabled={saving || (!dirty && !isNew)}
            >
              <Save className="h-4 w-4" aria-hidden />
              {saving ? 'Saving…' : isNew ? 'Create' : 'Save quiz'}
            </Button>
          </div>
        </div>

        {/* Main + sidebar */}
        <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
          {/* Questions */}
          <div className="flex flex-col gap-5">
            {draft.questions.map((q, i) => (
              <QuestionCard
                key={i}
                index={i}
                question={q}
                onQuestionChange={(patch) => updateQuestion(i, patch)}
                onOptionChange={(oi, patch) => updateOption(i, oi, patch)}
                onCorrectChange={(optionId) => setCorrect(i, optionId)}
              />
            ))}
          </div>

          {/* Sidebar */}
          <Sidebar draft={draft} setField={setField} />
        </div>
      </div>
    </AppShell>
  );
}

// -------------------------------------------------------------------------

function QuestionCard({
  index,
  question,
  onQuestionChange,
  onOptionChange,
  onCorrectChange,
}: {
  index: number;
  question: QuestionDraft;
  onQuestionChange: (patch: Partial<QuestionDraft>) => void;
  onOptionChange: (optionIndex: number, patch: Partial<OptionDraft>) => void;
  onCorrectChange: (optionId: string) => void;
}) {
  return (
    <section className="rounded-2xl border border-or-slate-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
          Question {index + 1}
        </div>
      </div>

      <textarea
        value={question.prompt}
        onChange={(e) => onQuestionChange({ prompt: e.target.value })}
        placeholder="What do you want the reader to think about?"
        rows={2}
        className="mt-2 w-full resize-y rounded-lg border border-or-slate-200 bg-white px-3 py-2 font-display text-[18px] leading-snug text-or-slate-900 focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
      />

      {/* Options */}
      <div className="mt-4 flex flex-col gap-2">
        {question.options.map((opt, oi) => (
          <div
            key={oi}
            className={cn(
              'flex items-center gap-2 rounded-lg border px-3 py-2',
              question.correctOptionId === opt.id
                ? 'border-or-green-700/50 bg-or-green-100/40'
                : 'border-or-slate-200 bg-white',
            )}
          >
            <label className="flex items-center gap-1.5 text-[12px] text-or-slate-600">
              <input
                type="radio"
                name={`q-${index}-correct`}
                checked={question.correctOptionId === opt.id}
                onChange={() => onCorrectChange(opt.id)}
                aria-label={`Mark option ${opt.id} correct`}
              />
              <span className="font-mono text-[11px] font-semibold uppercase text-or-slate-500">
                {opt.id}
              </span>
            </label>
            <input
              type="text"
              value={opt.emoji}
              onChange={(e) => onOptionChange(oi, { emoji: e.target.value })}
              placeholder="🙂"
              className="h-8 w-10 rounded border border-or-slate-200 bg-white text-center text-[14px] focus:border-or-orange-400 focus:outline-none"
              maxLength={4}
              aria-label={`Option ${opt.id} emoji (optional)`}
            />
            <input
              type="text"
              value={opt.text}
              onChange={(e) => onOptionChange(oi, { text: e.target.value })}
              placeholder={`Option ${opt.id.toUpperCase()}`}
              className="h-8 flex-1 rounded border border-or-slate-200 bg-white px-2 text-[13px] text-or-slate-900 focus:border-or-orange-400 focus:outline-none"
            />
          </div>
        ))}
      </div>

      {/* The per-question "Explanation" and "Callback" inputs were removed —
          these fields are no longer authored in the admin or shown to readers.
          Existing values are intentionally preserved: the draft still carries
          them (toDraft → questionsPayload) so editing a quiz round-trips them
          unchanged rather than nulling them out. */}
    </section>
  );
}

// -------------------------------------------------------------------------

function Sidebar({
  draft,
  setField,
}: {
  draft: QuizDraft;
  setField: <K extends keyof QuizDraft>(key: K, value: QuizDraft[K]) => void;
}) {
  return (
    <aside className="flex flex-col gap-4 rounded-2xl border border-or-slate-200 bg-white p-5">
      <Field label="Status">
        <div className="inline-flex rounded-full border border-or-slate-200 bg-or-slate-50 p-1">
          <StatusOption
            active={draft.status === 'pending'}
            onClick={() => setField('status', 'pending')}
            label="Pending"
          />
          <StatusOption
            active={draft.status === 'live'}
            onClick={() => setField('status', 'live')}
            label="Live"
          />
        </div>
      </Field>

      <Field label="Pass threshold" hint="Correct answers needed to earn the coin reward. 1–5.">
        <input
          type="number"
          min={1}
          max={5}
          value={draft.passThreshold}
          onChange={(e) =>
            setField('passThreshold', Math.max(1, Math.min(5, Number(e.target.value) || 1)))
          }
          className="h-9 w-20 rounded-lg border border-or-slate-200 bg-white px-3 text-[13px] focus:border-or-orange-400 focus:outline-none focus:ring-2 focus:ring-or-orange-400/30"
        />
      </Field>
    </aside>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-or-slate-500">
        {label}
      </div>
      {children}
      {hint ? <p className="text-[11px] text-or-slate-500">{hint}</p> : null}
    </div>
  );
}

function StatusOption({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-full px-3 py-1 text-[12px] font-semibold transition-colors',
        active ? 'bg-white text-or-slate-900 shadow-or-card' : 'text-or-slate-500',
      )}
    >
      {label}
    </button>
  );
}

// Silence the `Plus` import if we don't use it elsewhere (future-proofing
// for an "Add question" button). Keep available without triggering unused
// import lint errors.
void Plus;
